import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useDebounce } from "use-debounce";
import { QueryKeys, QueryTimes } from "~/application/constants";
import { ApiError, Customer, PaginatedResource } from "~/application/types";
import { customerService } from "~/application/usecases";
import { useLogTag } from "~/presentation/core/contexts/LogTagContext";
import { useUser } from "~/presentation/core/contexts/UserContext";
import {
  FilterCredit,
  FilterStatus,
  UseQueryCustomersOptions,
  UseQueryCustomersReturn,
} from "./types";
import { logError } from "../../utils/errors";

const SNACKBAR_MESSAGES = {
  LOAD_ERROR_MESSAGE: "Falha ao listar empresas",
} as const;

export function useQueryCustomers({
  options,
}: UseQueryCustomersOptions = {}): UseQueryCustomersReturn {
  const { contexts } = useUser();
  const { LOG_TAG } = useLogTag();

  const [searchText, setSearchTextState] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState<FilterStatus | undefined>();
  const [credit, setCredit] = useState<FilterCredit | undefined>();

  const [search] = useDebounce(searchText, 700);

  const statusValue = status?.value;
  const creditValue = credit?.value;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: onRefetch,
  } = useQuery<PaginatedResource<Customer[]>, ApiError>({
    queryKey: [
      QueryKeys.CUSTOMERS,
      contexts?.agency,
      currentPage,
      { search },
      statusValue,
      creditValue,
    ],
    queryFn: () =>
      customerService.find({
        page: currentPage,
        name: search,
        active: statusValue,
        max_credit_limit: creditValue,
      }),
    staleTime: QueryTimes.NORMAL,
    refetchOnWindowFocus: false,
    onError: (error) => {
      logError({
        error,
        logTag: LOG_TAG,
        defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
      });
    },
    ...options,
  });

  const handleSetText = useCallback((text: string) => {
    setSearchTextState(text);
    setCurrentPage(1);
  }, []);

  const handleSetStatus = useCallback((newStatus: FilterStatus) => {
    setStatus(newStatus);
    setCurrentPage(1);
  }, []);

  const handleSetCredit = useCallback((newCredit: FilterCredit) => {
    setCredit(newCredit);
    setCurrentPage(1);
  }, []);

  return {
    customers: data,
    isFetchingCustomers: isFetching,
    isLoadingCustomers: isLoading,
    isErrorCustomers: isError,
    currentPage,
    searchText,
    status,
    credit,
    setCurrentPage,
    setSearchText: handleSetText,
    setStatus: handleSetStatus,
    setCredit: handleSetCredit,
    onRefetch,
  };
}
